import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import zurich_picture from '../utils/static/cityscape-zurich-biggest-city-switzerland-aerial-view.jpg';
import cuentaconmigo_logo from '../utils/static/CuentaConmigo.png';
import acoge_logo from '../utils/static/ACOGE.png';
import podcast_logo from '../utils/static/PodcastLogo.png';
import {HeartIcon,BriefcaseIcon,ArrowTrendingUpIcon,InformationCircleIcon,CalendarIcon,MapPinIcon,UsersIcon,LightBulbIcon} from '@heroicons/react/24/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import ComunidadModal from '../components/modals/ComunidadModal';
import ProveedoresModal from '../components/modals/ProveedoresModal';

const photos = [
  'https://res.cloudinary.com/dommpgy1l/image/upload/v1784369658/IMG_1815_lirh4t.jpg',
  'https://res.cloudinary.com/dommpgy1l/image/upload/v1784370346/IMG_0871_eopi5s.jpg',
  'https://res.cloudinary.com/dommpgy1l/image/upload/v1784370346/IMG_0868_w9olxv.jpg',
];

const CAROUSEL_COUNT = 3;

const LandingPage = () => {
  const { t } = useTranslation();
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [proveedoresModalOpen, setProveedoresModalOpen] = useState(false);
  const [comunidadModalOpen, setComunidadModalOpen] = useState(false);

  const modalTriggerRef = useRef(null);
  const comunidadTriggerRef = useRef(null);

  const openProveedoresModal = () => setProveedoresModalOpen(true);
  const closeProveedoresModal = () => {
    setProveedoresModalOpen(false);
    modalTriggerRef.current?.focus();
  };

  const openComunidadModal = () => setComunidadModalOpen(true);
  const closeComunidadModal = () => {
    setComunidadModalOpen(false);
    comunidadTriggerRef.current?.focus();
  };

  const prevSlide = () => setCarouselIndex(i => (i - 1 + CAROUSEL_COUNT) % CAROUSEL_COUNT);
  const nextSlide = () => setCarouselIndex(i => (i + 1) % CAROUSEL_COUNT);

  return (
    <div className="bg-[#f0efed]">
      {/* Hero Section */}
      <div
        className="relative isolate px-6 pt-14 lg:px-8"
        style={{
          backgroundImage: `url(${zurich_picture})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100dvh',
        }}
      >
        {/* Note: Image from freepik: https://img.freepik.com/premium-photo/tourists-sidewalk-caf-city_1605434-35832.jpg?w=2000 */}

        {/* Overlay for Darkening the Image */}
        <div className="absolute inset-0 bg-black opacity-30" style={{ zIndex: 1 }} />

        {/* Content Section */}
        <div className="relative mx-auto max-w-2xl py-32 sm:py-48 lg:py-56 text-center" style={{ zIndex: 2 }}>
          <div className="hidden sm:mb-8 sm:flex sm:justify-center">
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
            {t('hero.subtitle')}
          </h1>
          <p className="mt-6 text-lg leading-8 text-white">
            {t('hero.title')}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <Link
              to="/map"
              className="inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur-sm px-5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-colors"
            >
              <MapPinIcon className="h-4 w-4" aria-hidden="true" />
              {t('hero.cta')}
            </Link>

            <Link
              to="/events"
              className="inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur-sm px-5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-colors"
            >
              <CalendarIcon className="h-4 w-4" aria-hidden="true" />
              {t('hero.learn')}
            </Link>

            <Link
              to="/map?suggest=true"
              className="inline-flex items-center gap-2 rounded-full bg-[#1d5cab] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#2372bd] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d5cab] transition-colors"
            >
              <LightBulbIcon className="h-4 w-4" aria-hidden="true" />
              {t('hero.suggest')}
            </Link>
          </div>
        </div>
      </div>


      {/* New Section: Una comunidad. Dos oportunidades. */}
      <div className="bg-[#f0efed] py-20 sm:py-28 border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center mb-16">
            <p className="mt-6 font-bold text-sm leading-6 text-[#1d5cab]">
              {t('landing.section-heading')}
            </p>
          </div>

          <div className="mx-auto grid max-w-none grid-cols-1 gap-8 lg:grid-cols-2">

            {/* Left Card - Comunidad */}
            <div className="flex flex-col justify-between rounded-2xl bg-white p-8 ring-1 ring-gray-900/5 shadow-md sm:p-10">
              <div>
                <p className="mt-6 font-bold text-sm leading-6 text-[#1d5cab]">
                  {t('landing.comunidad-label')}
                </p>
                <h3 className="text-2xl font-bold tracking-tight text-gray-900">
                  {t('landing.comunidad-heading')}
                </h3>
                <ul className="mt-6 text-sm leading-6 text-gray-600">
                  <li className="flex items-start gap-x-3">
                    <UsersIcon className="h-6 w-6 text-[#1d5cab] shrink-0" aria-hidden="true" />
                    <span>{t('landing.comunidad-item-1')}</span>
                  </li>
                  <li className="flex items-start gap-x-3">
                    <MapPinIcon className="h-6 w-6 text-[#1d5cab] shrink-0" aria-hidden="true" />
                    <span>{t('landing.comunidad-item-2')}</span>
                  </li>
                  <li className="flex items-start gap-x-3">
                    <CalendarIcon className="h-6 w-6 text-[#1d5cab] shrink-0" aria-hidden="true" />
                    <span>{t('landing.comunidad-item-3')}</span>
                  </li>
                  <li className="flex items-start gap-x-3">
                    <InformationCircleIcon className="h-6 w-6 text-[#1d5cab] shrink-0" aria-hidden="true" />
                    <span>{t('landing.comunidad-item-4')}</span>
                  </li>
                </ul>
              </div>
              <div className="mt-8">
                <button
                  ref={comunidadTriggerRef}
                  onClick={openComunidadModal}
                  className="block w-full rounded-md bg-[#1d5cab] px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-[#2372bd] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d5cab] transition-colors"
                >
                  {t('landing.comunidad-cta')}
                </button>
              </div>
            </div>

            {/* Right Card - Proveedores */}
            <div className="flex flex-col justify-between rounded-2xl bg-white p-8 ring-1 ring-gray-900/5 shadow-md sm:p-10">
              <div>
                <p className="mt-6 font-bold text-sm leading-6 text-[#F5751B]">
                  {t('landing.proveedores-label')}
                </p>
                <h3 className="text-2xl font-bold tracking-tight text-gray-900">
                  {t('landing.proveedores-heading')}
                </h3>
                <ul className="mt-6 text-sm leading-6 text-gray-600">
                  <li className="flex items-start gap-x-3">
                    <ArrowTrendingUpIcon className="h-6 w-6 text-[#F5751B] shrink-0" aria-hidden="true" />
                    <span>{t('landing.proveedores-item-1')}</span>
                  </li>
                  <li className="flex items-start gap-x-3">
                    <BriefcaseIcon className="h-6 w-6 text-[#F5751B] shrink-0" aria-hidden="true" />
                    <span>{t('landing.proveedores-item-2')}</span>
                  </li>
                  <li className="flex items-start gap-x-3">
                    <HeartIcon className="h-6 w-6 text-[#F5751B] shrink-0" aria-hidden="true" />
                    <span>{t('landing.proveedores-item-3')}</span>
                  </li>
                </ul>
              </div>
              <div className="mt-8">
                <button
                  ref={modalTriggerRef}
                  onClick={openProveedoresModal}
                  className="block w-full rounded-md bg-[#F5751B] px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-[#d4610e] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5751B] transition-colors"
                >
                  {t('landing.proveedores-cta')}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* NEW SECTION: Nuestro impacto (Dark Background matching header/hero styling) */}
      <div className="bg-gray-950 py-20 sm:py-28 text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <div className="mx-auto max-w-3xl mb-12">
            <p className="mt-6 font-bold text-sm leading-6 text-[#1d5cab]">
              {t('landing.impacto-heading')}
            </p>
            <p className="mt-4 text-lg leading-8 text-gray-300">
              <Trans
                i18nKey="landing.impacto-description"
                components={{ blue: <span className="text-[#1d5cab]" />, orange: <span className="text-[#F5751B]" /> }}
              />
            </p>
          </div>

          {/* Hero Carousel */}
          <div className="mt-10 relative w-full rounded-2xl overflow-hidden">
            {/* Slide */}
            <div className="w-full bg-gray-900 flex items-center justify-center">
              <img src={photos[carouselIndex]} className="w-full sm:w-3/4 md:w-1/2 h-auto" alt={t('landing.carousel-alt')} />
            </div>

            {/* Left arrow */}
            <button
              onClick={prevSlide}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/30 hover:bg-black/50 text-white rounded-full transition-colors"
              aria-label="Previous"
            >
              <ChevronLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>

            {/* Right arrow */}
            <button
              onClick={nextSlide}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/30 hover:bg-black/50 text-white rounded-full transition-colors"
              aria-label="Next"
            >
              <ChevronRightIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>

            {/* Indicator segments */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
              {[...Array(CAROUSEL_COUNT)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCarouselIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === carouselIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/40'}`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Botones apilados (Uno arriba, uno abajo) */}
          <div className="mt-12 flex flex-col items-center gap-4">
            <a
              href="https://kdrive.infomaniak.com/app/share/2643440/37deecab-c453-4061-a9c9-a0264540ac98"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full max-w-md rounded-md bg-[#1d5cab] px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#2372bd] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d5cab] transition-colors"
            >
              {t('landing.impacto-cta-latam')}
            </a>

            <a
              href="https://www.proclade.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full max-w-md rounded-md bg-transparent border border-[#1d5cab] px-5 py-3 text-sm font-semibold text-[#1d5cab] shadow-sm hover:bg-grey focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d5cab] transition-colors"
            >
              {t('landing.impacto-cta-world')}
            </a>
          </div>
        </div>
      </div>

      {/* Feature Section */}
      <div id="features" className="bg-[#f0efed] py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:text-center">
              <p className="mt-6 font-bold text-sm leading-6 text-[#1d5cab]">
                <Trans
                  i18nKey="feature.description"
                  components={{ historyLink: <Link to="/history" className="underline hover:text-[#2372bd]" /> }}
                />
              </p>
            </div>

            {/* Three-column grid container */}
            <div className="mx-auto grid max-w-none grid-cols-1 gap-8 lg:grid-cols-3 mt-16 sm:mt-20 lg:mt-24">

              {/* ITEM 1 - ACOGE */}
              <div className="flex flex-col justify-between rounded-2xl bg-white p-8 ring-1 ring-gray-900/5 shadow-md sm:p-10">
                <div>
                  <div className="flex items-center justify-center mb-6">
                    <img src={acoge_logo} alt="ACOGE Logo" className="h-12 w-auto object-contain" />
                  </div>
                  <p className="mt-4 text-sm leading-6 text-gray-600">{t('feature.acoge-description')}</p>
                </div>
                <div className="mt-8">
                  <a
                    href="https://acoge.ch"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full rounded-md bg-[#1d5cab] px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-[#2372bd] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d5cab]"
                  >
                    {t('feature.acoge-cta')}
                  </a>
                </div>
              </div>

              {/* ITEM 2 - CUENTA CONMIGO */}
              <div className="flex flex-col justify-between rounded-2xl bg-white p-8 ring-1 ring-gray-900/5 shadow-md sm:p-10">
                <div>
                  <div className="flex items-center justify-center mb-6">
                    <img src={cuentaconmigo_logo} alt="Cuenta Conmigo Logo" className="h-12 w-auto object-contain" />
                  </div>
                  <p className="mt-4 text-sm leading-6 text-gray-600">{t('feature.cuenta-description')}</p>
                </div>
                <div className="mt-8">
                  <a
                    href="https://cuentaconmigo.ch/ebook/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full rounded-md bg-[#1d5cab] px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-[#2372bd] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d5cab]"
                  >
                    {t('feature.cuenta-cta')}
                  </a>
                </div>
              </div>

              {/* ITEM 3 - LUGAR */}
              <div className="flex flex-col justify-between rounded-2xl bg-white p-8 ring-1 ring-gray-900/5 shadow-md sm:p-10">
                <div>
                  <div className="flex items-center justify-center mb-6">
                    <img src={podcast_logo} alt="Un lugar de afuera Logo" className="h-12 w-auto object-contain" />
                  </div>
                  <p className="mt-4 text-sm leading-6 text-gray-600">{t('feature.lugar-description')}</p>
                </div>
                <div className="mt-8">
                  <a
                    href="https://open.spotify.com/show/55RpurwryL8FOYXsRp9nn6"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full rounded-md bg-[#1d5cab] px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-[#2372bd] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1d5cab]"
                  >
                    {t('feature.lugar-cta')}
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>

      {/* New Section: Encuentra lo que buscas */}
      <div className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <div className="mx-auto max-w-3xl mb-12">
            <p className="mt-6 font-bold text-sm leading-6 text-[#1d5cab]">
              {t('landing.encuentra-heading')}
            </p>
          </div>
          <div className="mt-10 w-full sm:w-3/4 md:w-1/2 mx-auto">
            <img
              src="https://res.cloudinary.com/dommpgy1l/image/upload/v1784369118/mockup_laptop_mobile_xttbn6.png"
              alt=""
              className="w-full h-auto"
            />
          </div>
        </div>
      </div>

      {comunidadModalOpen && <ComunidadModal onClose={closeComunidadModal} />}
      {proveedoresModalOpen && <ProveedoresModal onClose={closeProveedoresModal} />}

    </div>
  );
};

export default LandingPage;
